import { ReactElement, forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import collect from 'collect.js';
import { CategoryInterface, ProductInterface } from '../../services/exports/Interfaces';
import CategoryItem from './category/CategoryItem';
import useScreenType from '../../hooks/utility/useScreenType';
import MenuBar from './MenuNavigationBar/MenuBar';
import MenuList from '../menu/MenuList';
import useMenu from '../../hooks/menu/useMenu';
import { debounce } from 'lodash';
import useAppState from '../../hooks/global/useAppState';
import classnames from 'classnames';
import useScrollPosition from '../../hooks/utility/useScrollPosition';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import { CompiledProduct, WithActualPrice } from '../../providers/MenuProvider';
import useOrderMethodsStatus from '../../hooks/availability/useOrderMethodsStatus';
import useCompanyHours from '../../hooks/availability/useCompanyHours';
import MostPopularProductsCarousel from '../carousel/most-popular-products';
import usePageHeader from '../../hooks/navigation/usePageHeader';

interface Props {
  className?: string;
  menuListClassName?: string;
  categoryListClassName?: string;
  ListHeaderComponent?: ReactElement;
  ListFooterComponent?: ReactElement;
  DiscountsComponent?: ReactElement;
  LastOrderComponent?: ReactElement;
  onProductClick?: (product: ProductInterface | null) => void;
}

const MenuCategoryList = forwardRef((props: Props, ref: any): ReactElement => {
  const {
    className,
    menuListClassName,
    categoryListClassName,
    ListHeaderComponent,
    ListFooterComponent,
    DiscountsComponent,
    LastOrderComponent,
    onProductClick,
  } = props;

  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { menu } = useAppState();
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:MenuCategoryList' });
  const { toggleMenuItemModal, toggleRestaurantClosedModal } = useAppMethods();
  const { categories, selectedMenu } = useMenu();
  const { isDesktop } = useScreenType();
  const { isMobileApp } = useMobileApp();
  const { scrollPosition } = useScrollPosition({ onScroll });
  const { isMobile } = useScreenType();
  const [searchParams] = useSearchParams();
  const { orderingDisabled } = useOrderMethodsStatus();
  const { ordersPausedUntil } = useCompanyHours();
  const { pageHeaderHeight } = usePageHeader();
  const searchString = searchParams.get('search');
  const filteredCategories = useMemo(
    () =>
      searchParams.has('search') && searchString.length
        ? categories.filter((category) =>
            category.products.some((product) => new RegExp(searchString, 'i').test(product.name)),
          )
        : categories,
    [categories, searchString],
  );

  const [focusedCategoryId, setFocusedCategoryId] = useState<number>(
    menu?.activeCategoryId ?? collect(filteredCategories)?.first()?.id,
  );

  const [activeScrollIndex, setActiveScrollIndex] = useState<number>(0);

  const categoriesRef = useRef(filteredCategories);
  categoriesRef.current = filteredCategories;

  const activeScrollIndexRef = useRef(activeScrollIndex);
  activeScrollIndexRef.current = activeScrollIndex;

  const scrollPositionRef = useRef(scrollPosition);
  scrollPositionRef.current = scrollPosition;

  const menuWrapperRef = useRef<HTMLDivElement>(null);
  const menuBarRef = useRef<HTMLDivElement>();

  useImperativeHandle(ref, () => ({
    toggleProductModal,
  }));

  function onScroll(): void {
    updateActiveCategory();
  }

  const updateActiveCategory = debounce(() => {
    const headerEndPosition =
      menuBarRef?.current?.getBoundingClientRect()?.top + menuBarRef?.current?.getBoundingClientRect()?.height + 50;

    const categoryDivs = document.querySelectorAll('#category-list > div');
    let activeCategory: CategoryInterface;

    for (let i = 0; i < categoryDivs.length; i++) {
      const category = categoryDivs[i];

      const categoryStartPosition = category?.getBoundingClientRect()?.top;
      const categoryEndPosition = category?.getBoundingClientRect()?.height;

      if (categoryStartPosition < headerEndPosition && headerEndPosition < categoryEndPosition) {
        activeCategory = filteredCategories[i];
      }
    }
    setFocusedCategoryId(activeCategory?.id);
  }, 200);

  function toggleProductModal(product: CompiledProduct): void {
    if (orderingDisabled(cached_order?.method) && company?.has_live_orders) {
      return toggleRestaurantClosedModal({ openModal: true });
    }

    if (onProductClick) {
      return onProductClick(product);
    }

    return toggleMenuItemModal({
      openModal: true,
      product: product,
      isFreeItem: product.free_eligible && !product.actual_price,
    });
  }

  function isActive(index: number): boolean {
    if (activeScrollIndexRef.current <= 3) {
      return index <= 6;
    }

    return index >= activeScrollIndexRef.current - 3 && index <= activeScrollIndexRef.current + 3;
  }

  const renderCategoryList = useMemo(() => {
    if (!filteredCategories.length) {
      return (
        <div className={classnames('my-md space-y-md', { 'mx-md': isMobile })}>
          <h4>{t('no_results.title', { search: searchString })}</h4>
          <p className="text-sm">{t('no_results.desc')}</p>
        </div>
      );
    }

    const renderCategory = (
      category: CategoryInterface<WithActualPrice<ProductInterface>>,
      index: number,
    ): JSX.Element => {
      return (
        <div
          key={`category-${category.id}`}
          className={classnames('lg:px-none px-md mb-xl', {
            'px-none !mb-md': isMobileApp,
          })}
          id={`category-id-${category?.id}`}
        >
          <CategoryItem
            category={category}
            index={index}
            isActive={isActive(index)}
            isDesktop={isDesktop}
            toggleProduct={toggleProductModal}
            onChangeVisibleIndex={setActiveScrollIndex}
          />
        </div>
      );
    };

    return (
      <div id="category-list" className={classnames(categoryListClassName, 'mt-lg')} key={selectedMenu?.id?.toString()}>
        {filteredCategories?.map(renderCategory)}
      </div>
    );
  }, [
    selectedMenu,
    filteredCategories,
    cached_order?.method,
    activeScrollIndexRef.current,
    isDesktop,
    categoryListClassName,
    cached_order?.method,
    onProductClick,
    orderingDisabled,
    company,
    ordersPausedUntil,
  ]);

  return (
    <>
      <div id={'menu-category-list'} className={className}>
        <div ref={menuWrapperRef}>
          {ListHeaderComponent && ListHeaderComponent}
          {!isMobileApp && <MostPopularProductsCarousel toggleProduct={toggleProductModal} className="mb-lg" />}
          <div className={classnames(menuListClassName, 'px-md lg:px-none')} id="menu-list">
            <MenuList />
          </div>
          <div
            className={classnames('w-full sticky z-50 mb-lg')}
            style={{ top: isDesktop ? pageHeaderHeight.desktop : 0 }}
            ref={menuBarRef}
          >
            <MenuBar focusedCategoryId={focusedCategoryId} categories={filteredCategories} />
          </div>
          {DiscountsComponent}
          {!searchString?.length && LastOrderComponent}
          {renderCategoryList}
          {ListFooterComponent && ListFooterComponent}
        </div>
      </div>
    </>
  );
});

export default MenuCategoryList;
